<template>
    <div class="mutagen-display mt-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h4 class="mb-0">Mutagens</h4>
            <button
                type="button"
                class="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#mutagenModal"
            >
                <font-awesome-icon icon="plus" class="me-1" /> Add
            </button>
        </div>

        <hr />

        <div class="row">
            <div
                class="col-sm-12 mb-3"
                v-for="(item, index) in mutagenList"
                :key="item.name"
            >
                <div class="d-flex justify-content-between align-items-center">
                    <strong
                        @click="selectedMutagen = item.name"
                        data-bs-toggle="modal"
                        data-bs-target="#viewMutagenModal"
                        style="cursor: pointer"
                    >
                        {{ item.name || "Unnamed Mutagen" }}
                    </strong>
                </div>
                <hr v-if="index < mutagenList.length - 1" />
            </div>
        </div>
    </div>

    <!-- Add Mutagen Modal -->
    <div
        class="modal fade"
        id="mutagenModal"
        tabindex="-1"
        aria-labelledby="mutagenModalLabel"
        aria-hidden="true"
    >
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h4 class="modal-title" id="mutagenModalLabel">
                        Add Mutagen
                    </h4>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                    ></button>
                </div>

                <div class="modal-body">
                    <div class="input-group mb-3">
                        <span class="input-group-text">Mutagen</span>
                        <select
                            class="form-select"
                            v-model="selectedMutagen"
                            @change="fillFromPreset"
                        >
                            <option value="">Select...</option>
                            <option
                                v-for="item in rosterStore.mutagens.sort(
                                    (a, b) => a.name.localeCompare(b.name)
                                )"
                                :key="item.name"
                                :value="item.name"
                            >
                                {{ item.name }}
                            </option>
                        </select>
                    </div>

                    <div v-if="newMutagen.name">
                        <h5>
                            <strong>{{ newMutagen.name }}</strong>
                        </h5>
                        <p>{{ newMutagen.description }}</p>
                        <p>
                            <strong>Mutation:</strong> {{ newMutagen.mutation }}
                        </p>
                        <p>
                            <strong>Evolved:</strong> {{ newMutagen.evolved }}
                        </p>
                    </div>
                </div>

                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal"
                    >
                        <font-awesome-icon icon="multiply" class="me-1" /> Close
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="addMutagen"
                    >
                        <font-awesome-icon icon="plus" class="me-1" /> Add
                        Mutagen
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- View Mutagen Modal -->
    <div
        class="modal fade"
        id="viewMutagenModal"
        tabindex="-1"
        aria-labelledby="viewMutagenModalLabel"
        aria-hidden="true"
    >
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h4
                        class="modal-title"
                        id="viewMutagenModalLabel"
                        v-if="selectedMutagen"
                    >
                        {{ selectedMutagen }}
                    </h4>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                    ></button>
                </div>
                <div class="modal-body" v-if="currentMutagen">
                    <h5>
                        <strong>{{ currentMutagen.name }}</strong>
                    </h5>
                    <p>{{ currentMutagen.description }}</p>
                    <p>
                        <strong>Mutation:</strong> {{ currentMutagen.mutation }}
                    </p>
                    <p>
                        <strong>Evolved:</strong> {{ currentMutagen.evolved }}
                    </p>
                </div>
                <div class="modal-footer">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal"
                    >
                        <font-awesome-icon icon="multiply" class="me-1" /> Close
                    </button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        @click="deleteMutagen(currentMutagen?.name)"
                        data-bs-dismiss="modal"
                    >
                        <font-awesome-icon icon="trash-alt" class="me-1" />
                        Remove Mutagen
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useRosterStore } from "../stores/index";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
    name: "MutagenDisplay",
    components: { FontAwesomeIcon },
    props: {
        character: { type: Object, required: true },
    },
    data() {
        return {
            selectedMutagen: null,
            newMutagen: {
                name: "",
                description: "",
                mutation: "",
                evolved: "",
            },
        };
    },
    computed: {
        ...mapStores(useRosterStore),
        mutagenList() {
            if (!this.character.mutagens) {
                this.character.mutagens = []; // Vue 3 reactivity handles this
            }
            return this.character.mutagens;
        },
        currentMutagen() {
            return (
                this.mutagenList.find((m) => m.name === this.selectedMutagen) ||
                null
            );
        },
    },
    methods: {
        addMutagen() {
            if (!this.newMutagen.name) {
                alert("Please select a mutagen!");
                return;
            }
            if (!Array.isArray(this.character.mutagens)) {
                this.character.mutagens = [];
            }
            if (
                this.character.mutagens.find(
                    (m) => m.name === this.newMutagen.name
                )
            ) {
                alert("This mutagen is already added!");
                return;
            }
            // deep clone so it’s independent of the compendium
            this.character.mutagens.push(
                JSON.parse(JSON.stringify(this.newMutagen))
            );
            this.selectedMutagen = null;
            this.resetNewMutagen();
            alert("Mutagen added!");
        },
        deleteMutagen(name) {
            if (!name) return;
            if (!Array.isArray(this.character.mutagens)) return;
            if (confirm(`Are you sure you want to delete ${name}?`)) {
                const index = this.character.mutagens.findIndex(
                    (m) => m.name === name
                );
                if (index !== -1) this.character.mutagens.splice(index, 1);
                this.selectedMutagen = null;
            }
        },
        fillFromPreset() {
            if (
                !this.selectedMutagen ||
                !Array.isArray(this.rosterStore.mutagens)
            ) {
                this.resetNewMutagen();
                return;
            }
            const preset = this.rosterStore.mutagens.find(
                (m) => m.name === this.selectedMutagen
            );
            if (preset) {
                this.newMutagen = JSON.parse(JSON.stringify(preset));
            }
        },
        resetNewMutagen() {
            this.newMutagen = {
                name: "",
                description: "",
                mutation: "",
                evolved: "",
            };
        },
    },
};
</script>

<style scoped>
.mutagen-display {
    padding: 10px 5px;
    border: solid 2px #555;
    border-radius: 8px;
}
</style>

